import classNames from 'classnames';

import { Token as Color } from 'export/brand0/colors';
import React, { forwardRef, ForwardRefExoticComponent, HTMLAttributes, RefAttributes } from 'react';

import { Icon } from '../icon';
import { cropText } from '../lib/crop-text';
import { Typography } from '../typography';

import styles from './tabs.scss';

type TabsData = {
  icon?: string;
  name: string;
  disabled?: boolean;
  iconColor?: Color;
};

type Props = RefAttributes<HTMLDivElement> &
  HTMLAttributes<HTMLDivElement> & {
    tabs: TabsData[];
    selectedIndex?: number;
    setSelectedIndex?: (index: number) => void;
    maxLength?: number;
    dataTestId?: string;
  };

const COMPONENT_ID = 'modulor-tabs';

export const Tabs: ForwardRefExoticComponent<Props> = forwardRef<HTMLDivElement, Props>(
  (
    {
      tabs = [],
      selectedIndex,
      maxLength = 15,
      setSelectedIndex,
      className,
      dataTestId = COMPONENT_ID,
      ...rest
    },
    ref,
  ) => {
    if (tabs.length === 0) return null;
    return (
      <div
        data-testid={dataTestId}
        data-id={dataTestId}
        data-component-name={COMPONENT_ID}
        ref={ref}
        className={classNames(className, styles.container)}
        {...rest}>
        {tabs.map((item, index) => {
          const active = selectedIndex === index;
          const labelVariant = active ? 'subhead-semibold' : 'subhead-regular';

          return item.name ? (
            <button
              type="button"
              className={classNames(className, styles.tab, {
                [styles.withIcon]: !!item.icon,
                [styles.active]: active,
              })}
              data-testid={`${dataTestId}-button`}
              onClick={() => {
                setSelectedIndex(index);
              }}
              key={item.name + index}
              disabled={!!item.disabled}>
              {item.icon ? (
                <Icon
                  name={item.icon}
                  fill={item.iconColor}
                  className={styles.icon}
                  dataTestId={`${dataTestId}-icon`}
                  disabled={!!item.disabled}
                />
              ) : null}
              <Typography
                className={styles.name}
                variant={labelVariant}
                dataTestId={`${dataTestId}-typography`}>
                {cropText(item.name, maxLength)}
              </Typography>
            </button>
          ) : null;
        })}
      </div>
    );
  },
);

Tabs.displayName = 'Tabs';
